// src/components/fraud/FraudCharts.js
// Charts section: Fraud Trend (7 days) + Action Breakdown
// Uses pure SVG / CSS bar charts — no external charting library needed.

import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';

// ─── Mini Bar Chart (SVG) ─────────────────────────────────────────────────────
const BarChart = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="d-flex align-items-end gap-2 px-2" style={{ height: 120 }}>
        {Array.from({ length: 7 }).map((_, i) => (
          <div
            key={i}
            className="fraud-skeleton flex-grow-1"
            style={{ height: `${30 + Math.random() * 70}%`, borderRadius: '4px 4px 0 0' }}
          />
        ))}
      </div>
    );
  }

  const maxVal = Math.max(...data.map((d) => d.count), 1);
  const chartH = 110;

  return (
    <div style={{ overflowX: 'auto' }}>
      <svg
        width="100%"
        height={chartH + 30}
        viewBox={`0 0 ${data.length * 50} ${chartH + 30}`}
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#D9FF1F" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#D9FF1F" stopOpacity="0.2" />
          </linearGradient>
          <linearGradient id="barGradHigh" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#EF4444" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#EF4444" stopOpacity="0.2" />
          </linearGradient>
        </defs>

        {data.map((item, i) => {
          const barH = maxVal > 0 ? (item.count / maxVal) * chartH : 4;
          const x = i * 50 + 8;
          const y = chartH - barH;
          const isMax = item.count === maxVal && maxVal > 0;
          return (
            <g key={i}>
              <rect
                x={x}
                y={y}
                width={34}
                height={Math.max(barH, 4)}
                rx={4}
                fill={isMax ? 'url(#barGradHigh)' : 'url(#barGrad)'}
              />
              {/* count label */}
              {item.count > 0 && (
                <text
                  x={x + 17}
                  y={y - 4}
                  textAnchor="middle"
                  fontSize={9}
                  fill="var(--text-secondary)"
                >
                  {item.count}
                </text>
              )}
              {/* day label */}
              <text
                x={x + 17}
                y={chartH + 20}
                textAnchor="middle"
                fontSize={9}
                fill="var(--text-secondary)"
              >
                {item.label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

// ─── Action Breakdown Donut / Pill Bars ───────────────────────────────────────
const ACTION_META = {
  ALLOW: { color: '#22C55E', label: 'Allow' },
  LOG: { color: '#D9FF1F', label: 'Log' },
  REVIEW: { color: '#F59E0B', label: 'Review' },
  BLOCK: { color: '#EF4444', label: 'Block' },
};

const ActionBreakdown = ({ data, loading }) => {
  const total = Object.values(data).reduce((s, v) => s + v, 0);

  if (loading) {
    return (
      <div className="d-flex flex-column gap-3 mt-2">
        {Object.keys(ACTION_META).map((k) => (
          <div key={k}>
            <div className="d-flex justify-content-between mb-1">
              <div className="fraud-skeleton" style={{ width: 60, height: 12, borderRadius: 4 }} />
              <div className="fraud-skeleton" style={{ width: 30, height: 12, borderRadius: 4 }} />
            </div>
            <div className="fraud-skeleton" style={{ width: '100%', height: 10, borderRadius: 6 }} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="d-flex flex-column gap-3 mt-2">
      {Object.entries(ACTION_META).map(([key, meta]) => {
        const count = data[key] || 0;
        const pct = total > 0 ? Math.round((count / total) * 100) : 0;
        return (
          <div key={key}>
            <div className="d-flex justify-content-between align-items-center mb-1">
              <div className="d-flex align-items-center gap-2">
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: meta.color,
                    display: 'inline-block',
                    flexShrink: 0,
                  }}
                />
                <small style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{meta.label}</small>
              </div>
              <small style={{ color: meta.color, fontWeight: 700, fontSize: 12 }}>
                {count} <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>({pct}%)</span>
              </small>
            </div>
            <div
              style={{
                width: '100%',
                height: 8,
                background: 'rgba(255,255,255,0.06)',
                borderRadius: 6,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${pct}%`,
                  height: '100%',
                  background: meta.color,
                  borderRadius: 6,
                  transition: 'width 0.6s ease',
                }}
              />
            </div>
          </div>
        );
      })}

      {total === 0 && (
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, textAlign: 'center', marginTop: 12 }}>
          No action data yet
        </p>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const FraudCharts = ({ trendData, actionBreakdown, loading }) => (
  <Container fluid className="px-4 my-4">
    <Row className="g-3">
      {/* Fraud Trend */}
      <Col xs={12} lg={7}>
        <div
          id="fraud-trend-chart"
          className="theme-card rounded-3 p-4 h-100"
          style={{ minHeight: 220 }}
        >
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0 text-h2" style={{ fontSize: 15 }}>
              Fraud Trend
            </h6>
            <span
              style={{
                fontSize: 11,
                color: 'var(--text-secondary)',
                background: 'rgba(255,255,255,0.06)',
                borderRadius: 20,
                padding: '2px 10px',
              }}
            >
              Last 7 days
            </span>
          </div>
          <BarChart data={trendData} loading={loading} />
        </div>
      </Col>

      {/* Action Breakdown */}
      <Col xs={12} lg={5}>
        <div
          id="fraud-action-breakdown"
          className="theme-card rounded-3 p-4 h-100"
          style={{ minHeight: 220 }}
        >
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0 text-h2" style={{ fontSize: 15 }}>
              Fraud Actions Breakdown
            </h6>
          </div>
          <ActionBreakdown data={actionBreakdown} loading={loading} />
        </div>
      </Col>
    </Row>
  </Container>
);

export default FraudCharts;
